import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * ClimateWidget
 *
 * Showcases farm weather trends, soil type, and notable climate challenges.
 * Renders a temperature mini-chart, key climate stats, and soil badge.
 */

const TempChart = ({ data, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true });
  if (!data || data.length < 2) return null;

  const maxH = Math.max(...data.map((d) => d.high));
  const minL = Math.min(...data.map((d) => d.low));
  const range = maxH - minL || 1;
  const w = 280;
  const h = 56;
  const pad = 4;

  const highPoints = data.map((d, i) => ({
    x: pad + (i / (data.length - 1)) * (w - pad * 2),
    y: pad + ((maxH - d.high) / range) * (h - pad * 2),
  }));
  const lowPoints = data.map((d, i) => ({
    x: pad + (i / (data.length - 1)) * (w - pad * 2),
    y: pad + ((maxH - d.low) / range) * (h - pad * 2),
  }));

  const highPath = highPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const lowPath = lowPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // Filled area between high and low
  const areaPath = highPath + " " + lowPoints.map((p, i) => `${i === 0 ? "L" : "L"} ${p.x} ${p.y}`).reverse().join(" ") + " Z";

  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="xMidYMid meet">
        {inView && (
          <>
            <motion.path
              d={areaPath}
              fill="#f59e0b"
              opacity={0.08}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.08 }}
              transition={{ duration: 0.6 }}
            />
            <motion.path
              d={highPath}
              fill="none"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, ease: "easeInOut" }}
            />
            <motion.path
              d={lowPath}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={2}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.2, ease: "easeInOut" }}
            />
          </>
        )}
      </svg>
      {/* Month labels */}
      <div className={`flex justify-between px-1 ${isArabic ? "flex-row-reverse" : ""}`}>
        {data.filter((_, i) => i % Math.max(1, Math.floor(data.length / 6)) === 0 || i === data.length - 1).map((d) => (
          <span key={d.month} className="text-[8px] text-slate-400 font-medium">{d.month}</span>
        ))}
      </div>
    </div>
  );
};

const ClimateWidget = ({ climate, isArabic }) => {
  if (!climate) return null;

  const stats = [
    { icon: "🌡️", label: isArabic ? "متوسط الحرارة" : "Avg Temp", value: climate.avgTemp },
    { icon: "💧", label: isArabic ? "الرطوبة" : "Humidity", value: climate.humidity },
    { icon: "🌧️", label: isArabic ? "أمطار سنوية" : "Annual Rain", value: climate.annualRainfall },
    { icon: "💨", label: isArabic ? "سرعة الرياح" : "Wind Speed", value: climate.windSpeed },
  ];

  if (climate.evapotranspiration) {
    stats.push({ icon: "☀️", label: isArabic ? "التبخر" : "ET Rate", value: climate.evapotranspiration });
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden h-full">
      {/* Header */}
      <div className={`px-5 py-4 border-b border-slate-100 flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center text-lg flex-shrink-0">🌤️</div>
        <div className={isArabic ? "text-right" : ""}>
          <h3 className="text-sm font-bold text-slate-900 mb-0">
            {isArabic ? "المناخ والبيئة" : "Climate & Environment"}
          </h3>
          <p className="text-[11px] text-slate-400 mb-0">
            {isArabic ? "اتجاهات الطقس ونوع التربة" : "Weather trends & soil type"}
          </p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Soil type badge */}
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="text-sm">🏜️</span>
          <div className={isArabic ? "text-right" : ""}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">
              {isArabic ? "نوع التربة" : "Soil Type"}
            </p>
            <p className="text-sm font-bold text-slate-800 mb-0">{climate.soilType}</p>
          </div>
        </div>

        {/* Temperature trend chart */}
        {climate.monthlyTemp && climate.monthlyTemp.length > 0 && (
          <div>
            <div className={`flex items-center justify-between mb-2 ${isArabic ? "flex-row-reverse" : ""}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">
                {isArabic ? "اتجاه الحرارة" : "Temperature Trend"}
              </p>
              <div className={`flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-0.5 rounded bg-amber-500 inline-block" />
                  <span className="text-[8px] text-slate-400">{isArabic ? "عليا" : "High"}</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-0.5 rounded bg-blue-500 inline-block" />
                  <span className="text-[8px] text-slate-400">{isArabic ? "دنيا" : "Low"}</span>
                </span>
              </div>
            </div>
            <TempChart data={climate.monthlyTemp} isArabic={isArabic} />
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-2">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
              <div className={`flex items-center gap-1.5 mb-0.5 ${isArabic ? "flex-row-reverse" : ""}`}>
                <span className="text-xs">{stat.icon}</span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{stat.label}</span>
              </div>
              <p className={`text-sm font-bold text-slate-800 mb-0 ${isArabic ? "text-right" : ""}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Challenges */}
        {climate.challenges && climate.challenges.length > 0 && (
          <div>
            <p className={`text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 ${isArabic ? "text-right" : ""}`}>
              {isArabic ? "تحديات مناخية" : "Climate Challenges"}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {climate.challenges.map((ch) => (
                <span key={ch} className="text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-100 px-2 py-1 rounded-full">
                  ⚠️ {ch}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClimateWidget;
